<template>
  <wizard-form v-on="$listeners" v-bind="$attrs">
    <template #content>
      <v-row>
        <v-col cols="12">
          <h1 class="primary--text font-weight-medium">
            {{ $t('transfer_international_account') }}
          </h1>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_alias')"
            rules="required|max:200"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_alias')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.alias"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="200"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_type')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-select
              v-model="permanentInstruction.accountType"
              :items="items"
              :label="`${$t('transfer_account_type')} *`"
              :error-messages="errors[0]"
              hide-details="auto"
              dense
              return-object
              @input="handleAccountType"
            >
              <template #item="{ item }">
                <span>{{ $t(item.t) }}</span>
              </template>

              <template #selection="{ item }">
                <span>{{ $t(item.t) }}</span>
              </template>
            </v-select>
          </ValidationProvider>
        </v-col>
        <v-col
          v-if="showBeneficiariesSelect"
          cols="12"
          md="6"
          class="d-flex flex-grow-1"
        >
          <ValidationProvider
            :name="$t('transfer_name')"
            rules="required|max:150"
            v-slot="{ errors }"
            class="flex-grow-1"
          >
            <v-select
              :label="`${$t('transfer_name')} *`"
              v-model="permanentInstruction.beneficiary"
              :items="beneficiaries"
              dense
              autocomplete="nope"
              :error-messages="errors[0]"
              hide-details="auto"
            />
          </ValidationProvider>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                v-if="lockedField"
                icon
                v-on="on"
                @click="handleUnlock"
                :disabled="!lockedField"
              >
                <v-icon>mdi-lock-open-variant</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('modify_beneficiary') }}</span>
          </v-tooltip>
        </v-col>
        <v-col v-else :cols="12" :md="6" class="d-flex flex-grow-1">
          <ValidationProvider
            class="flex-grow-1"
            :name="$t('transfer_name')"
            rules="required|max:200"
            v-slot="{ errors }"
          >
            <v-text-field
              class="flex-grow-1"
              ref="beneficiary"
              :label="`${$t('transfer_name')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.beneficiary"
              :error-messages="errors[0]"
              hide-details="auto"
              :disabled="lockedField"
              maxlength="200"
              @input="handleInputs"
            />
          </ValidationProvider>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                v-if="lockedField"
                icon
                v-on="on"
                @click="handleUnlock"
                :disabled="!lockedField"
              >
                <v-icon>mdi-lock-open-variant</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('modify_beneficiary') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row v-if="isTypeNoBank">
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_number_iban')"
            rules="required|alpha_num|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_account_number_iban')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankAccNumber"
              :error-messages="errors[0]"
              hide-details="auto"
              @input="handleInputs"
              maxlength="50"
              v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_address_1')"
            rules="required|max:50"
            vid="bnfAddres1"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_address_1')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfAddres1"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_address_2')"
            rules="max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_address_2')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfAddres2"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_country')"
            rules="required"
            vid="bnfCountry"
            v-slot="{ errors }"
          >
            <v-select
              v-model="permanentInstruction.bnfCountry"
              :items="countries"
              item-text="countryName"
              item-value="countryName"
              :label="`${$t('transfer_country')} *`"
              @input="selectCountry($event, 'bnfCountryCode')"
              :error-messages="errors[0]"
              hide-details="auto"
              dense
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_state')"
            rules="required"
            vid="bnfState"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_state')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfState"
              maxlength="50"
              hide-details="auto"
              :error-messages="errors[0]"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_city')"
            rules="required|max:50"
            vid="bnfCity"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_city')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfCity"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_zip_code')"
            rules="max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_zip_code')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfZipcode"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$t('transfer_phone')"
            dense
            autocomplete="nope"
            v-model="permanentInstruction.bnfPhone"
            maxlength="50"
            hide-details="auto"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_financial_institution_of_beneficiary')"
            rules="required|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="
                `${$t('transfer_financial_institution_of_beneficiary')} *`
              "
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankName"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col v-if="!isTypeNoBank" cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_number_iban')"
            rules="required|alpha_num|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_account_number_iban')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankAccNumber"
              :error-messages="errors[0]"
              @input="handleInputs"
              maxlength="50"
              v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row v-if="permanentInstruction.accountType.value == 'nfiAccount'">
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_receiving_bank')"
            rules="required|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_receiving_bank')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.nfiName"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_account_receiving_bank')"
            rules="required|alpha_num|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="`${$t('transfer_account_receiving_bank')} *`"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.nfiAccountNumber"
              :error-messages="errors[0]"
              maxlength="50"
              v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_beneficiary_bank_aba')"
            rules="required|alpha_num|max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              label="ABA / SWIFT *"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankABA"
              maxlength="50"
              :error-messages="errors[0]"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_address_1')"
            rules="max:50"
            vid="bnfBankAddres1"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_address_1')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankAddres1"
              :error-messages="errors[0]"
              maxlength="50"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_address_2')"
            rules="max:50"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_address_2')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankAddres2"
              :error-messages="errors[0]"
              maxlength="50"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_beneficiary_bank_country')"
            rules="required"
            vid="bnfBankCountry"
            v-slot="{ errors }"
          >
            <v-select
              v-model="permanentInstruction.bnfBankCountry"
              :items="countries"
              item-text="countryName"
              item-value="countryName"
              :label="`${$t('transfer_country')} *`"
              :error-messages="errors[0]"
              @input="selectCountry($event, 'bnfBankCountryCode')"
              dense
              maxlength="50"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_state')"
            rules="max:50"
            vid="bnfBankState"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_state')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankState"
              :error-messages="errors[0]"
              maxlength="50"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_city')"
            rules="max:50"
            vid="bnfBankCity"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t('transfer_city')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.bnfBankCity"
              :error-messages="errors[0]"
              maxlength="50"
              hide-details="auto"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12">
          <v-text-field
            :label="$t('transfer_observations')"
            dense
            autocomplete="nope"
            v-model="permanentInstruction.bnfReference"
            :disabled="permanentInstruction.accountType.value == 'nfiAccount'"
            maxlength="128"
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            :label="$t('transfer_instructions')"
            dense
            autocomplete="nope"
            v-model="permanentInstruction.avsInternalRemark"
            maxlength="128"
            hide-details="auto"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-switch
            v-model="permanentInstruction.intermBankIsUsed"
            inset
            dense
            @change="handleSwitch"
            hide-details="auto"
          >
            <template #label>
              <span class="primary--text font-weight-medium">
                {{ $t('transfer_intermediary') }}
              </span>
            </template>
          </v-switch>
        </v-col>
      </v-row>
      <template v-if="permanentInstruction.intermBankIsUsed">
        <v-row>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_intermediary')"
              rules="required|max:50"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="`${$t('transfer_intermediary')} *`"
                dense
                autocomplete="nope"
                v-model="permanentInstruction.intermBankName"
                :error-messages="errors[0]"
                maxlength="50"
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_intermediary_account')"
              rules="alpha_num|max:50"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="`${$t('transfer_intermediary_account')}`"
                dense
                autocomplete="nope"
                v-model="permanentInstruction.intermBankAccNumber"
                :error-messages="errors[0]"
                maxlength="50"
                v-mask="'XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'"
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_intermediary_bank_aba')"
              rules="required|max:50"
              v-slot="{ errors }"
            >
              <v-text-field
                label="ABA / SWIFT *"
                dense
                autocomplete="nope"
                v-model="permanentInstruction.intermBankABA"
                :error-messages="errors[0]"
                maxlength="50"
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_address_1')"
              rules="max:50"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="$t('transfer_address_1')"
                dense
                autocomplete="nope"
                v-model="permanentInstruction.intermBankAddres1"
                :error-messages="errors[0]"
                maxlength="50"
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_address_2')"
              rules="max:50"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="$t('transfer_address_2')"
                dense
                autocomplete="nope"
                v-model="permanentInstruction.intermBankAddres2"
                :error-messages="errors[0]"
                maxlength="50"
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_intermediary_bank_country')"
              rules="required"
              v-slot="{ errors }"
            >
              <v-select
                v-model="permanentInstruction.intermBankCountry"
                :items="countries"
                item-text="countryName"
                item-value="countryName"
                :label="`${$t('transfer_country')} *`"
                :error-messages="errors[0]"
                @input="selectCountry($event, 'intermBankCountryCode')"
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_state')"
              rules="max:50"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="$t('transfer_state')"
                dense
                autocomplete="nope"
                v-model="permanentInstruction.intermBankState"
                :error-messages="errors[0]"
                maxlength="50"
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider
              :name="$t('transfer_city')"
              rules="max:50"
              v-slot="{ errors }"
            >
              <v-text-field
                :label="$t('transfer_city')"
                dense
                autocomplete="nope"
                v-model="permanentInstruction.intermBankCity"
                :error-messages="errors[0]"
                maxlength="50"
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12">
            <v-text-field
              :label="$t('transfer_intermediary_observations')"
              dense
              autocomplete="nope"
              v-model="permanentInstruction.intermReference"
              maxlength="128"
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </template>
    </template>
  </wizard-form>
</template>
<script>
import { mapState } from 'vuex'
import WizardForm from '@/components/commons/WizardForm'
export default {
  components: {
    WizardForm
  },
  props: {
    permanentInstruction: {
      type: [Object]
    },
    partnerBanks: {
      type: Array,
      required: true
    },
    countries: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      lockedField: true,
      anotherBank: true,
      showBeneficiariesSelect: true,
      partnerBank: 'Otra',
      items: [
        { value: 'intlBankAccount', t: 'transfer_account_type_4' },
        { value: 'nfiAccount', t: 'transfer_account_type_3' }
      ],
      banks: ['Banco Mercantil', 'Banesco'],
      isThirdParty: false
    }
  },
  computed: {
    ...mapState('account', ['account', 'accountData']),
    ...mapState('auth', ['user']),
    internationalBanks() {
      const banks = this.partnerBanks.filter(bank => bank.prtBankType == 'intl')
      banks.push({ prtBankName: this.$t('transfer_other') })
      return banks
    },
    isExecutive() {
      return (
        this.user?.authorityLevelsCode === 1 ||
        this.user?.authorityLevelsCode === 2 ||
        this.user?.authorityLevelsCode === 4
      )
    },
    isAccountCode() {
      return (
        this.user?.avobjects?.findIndex(
          item =>
            item.code === this.account.code && item.typeCode === 'accountCode'
        ) > -1
      )
    },
    beneficiaries() {
      let items = []
      const main = this.accountData.firstname + ' ' + this.accountData.lastname
      items.push(main)

      if (this.accountData.type.toUpperCase() === 'JURIDICA') {
        const beneficiaries = this.accountData.beneficiaries
          .map(item => item.firstname + ' ' + item.lastname)
          .filter(item => item.trim() !== '')

        items = items.concat(beneficiaries)
      } else if (
        this.accountData.coOwners &&
        this.accountData.coOwners !== ''
      ) {
        items.push(this.accountData.coOwners)
      }

      return items
    },
    isTypeNoBank() {
      return (
        this.permanentInstruction?.accountType.t === 'transfer_account_type_3'
      )
    }
  },
  methods: {
    setBeneficiary() {
      this.permanentInstruction.isThirdParty = this.isThirdParty
    },
    handleAccountType() {
      this.partnerBank = ''
      if (this.permanentInstruction.accountType.value == 'nfiAccount') {
        this.$emit('changeAccType')
      } else {
        this.clear()
      }
      this.setBeneficiary()
    },
    handleInputs() {
      if (this.permanentInstruction.accountType.value == 'nfiAccount')
        this.$emit('changeAccType')
    },
    handleSwitch() {
      if (!this.permanentInstruction.intermBankIsUsed)
        this.$emit('clearIntermediary')
    },
    selectCountry(country, model) {
      const selectedCountry = this.countries.find(
        item => item.countryName == country
      )
      this.$emit('selectCountry', {
        model: model,
        code: selectedCountry.countryCode
      })
    },
    handlePartnerBank(bank) {
      if (bank == this.$t('transfer_other')) {
        this.anotherBank = true
        this.$emit('clearBank')
      } else {
        this.anotherBank = false
        this.$emit('selectPartnerBank', bank)
      }
    },
    async handleUnlock() {
      const text = !this.isExecutive
        ? this.$t('warning_instructions_payments_to_third_parties_customer')
        : this.$t('warning_instructions_payments_to_third_parties_1') +
          ' ' +
          this.$t('warning_instructions_payments_to_third_parties_2')

      const params = {
        text,
        title: this.$t('twofactor_modal_warning_title'),
        showClose: false,
        persistent: true,
        actions: {
          true: {
            text: this.$t('btn_accept'),
            color: 'primary'
          }
        }
      }

      await this.$dialog.info(params)

      if (this.isExecutive) {
        this.isThirdParty = true
        this.lockedField = false
        this.showBeneficiariesSelect = false
        this.permanentInstruction.beneficiary = ''
        this.permanentInstruction.bnfEmail = ''
        this.permanentInstruction.bnfEmailConfirm = ''
        this.permanentInstruction.isThirdParty = true
        // Set focus
        this.$nextTick(() => this.$refs.beneficiary.focus())
      }
    },
    async clear() {
      this.permanentInstruction.bnfAddres1 = ''
      this.permanentInstruction.bnfAddres2 = ''
      this.permanentInstruction.bnfZipcode = ''
      this.permanentInstruction.bnfCity = ''
      this.permanentInstruction.bnfState = ''
      this.permanentInstruction.bnfCountry = ''
      this.permanentInstruction.bnfCountryCode = ''
      this.permanentInstruction.bnfPhone = ''
      this.permanentInstruction.bnfFurtherCredit = ''
      this.permanentInstruction.bnfBankName = ''
      this.permanentInstruction.bnfBankABA = ''
      this.permanentInstruction.bnfBankAddres1 = ''
      this.permanentInstruction.bnfBankAddres2 = ''
      this.permanentInstruction.bnfBankCity = ''
      this.permanentInstruction.bnfBankState = ''
      this.permanentInstruction.bnfBankCountry = ''
      this.permanentInstruction.bnfBankCountryCode = ''
      this.permanentInstruction.bnfReceivingBank = ''
      this.permanentInstruction.bnfReceivingAccNumber = ''
      this.permanentInstruction.bnfReference = ''
      this.permanentInstruction.nfiName = ''
      this.permanentInstruction.nfiAccountNumber = ''
      this.permanentInstruction.intermBankIsUsed = false
      this.permanentInstruction.intermBankName = ''
      this.permanentInstruction.intermBankAccNumber = ''
      this.permanentInstruction.intermBankABA = ''
      this.permanentInstruction.intermBankAddres1 = ''
      this.permanentInstruction.intermBankAddres2 = ''
      this.permanentInstruction.intermBankCity = ''
      this.permanentInstruction.intermBankState = ''
      this.permanentInstruction.intermBankCountry = ''
      this.permanentInstruction.intermBankCountryCode = ''
      this.permanentInstruction.intermReference = ''
    }
  }
}
</script>
<style scoped>
.main-container {
  line-height: normal;
}

hr {
  margin: 0;
  padding: 0;
}

hr.rounded {
  border-top: 2px solid;
  width: 450px;
}
</style>
